<template>
  <div class="mc-menu">
    <sl-dropdown ref="dropdownComponent" hoist :distance="menuDistance" :placement="placement" @sl-hide="handleDropdownHide">
      <div slot="trigger" 
        @click.stop="handleButtonActionClick"
        @mousemove="handleButtonActionMouseMove"
        @mouseleave="handleButtonActionMouseLeave"
      >
        <slot v-if="hasButtonActionSlot" name="buttonAction"></slot>
        <sl-tooltip v-else ref="tooltipButtonAction" hoist
          :content="buttonActionTooltipText" 
          :trigger="buttonActionTooltipTrigger"  
          :placement="buttonActionTooltipPlacement"
        >
          <span :class="['button-action', buttonActionIcon, isMenuOpen ? 'active' : '']"></span>
        </sl-tooltip>
      </div>
      <div class="menu-section">
        <div v-if="hasSearch" class="search-input-wrapper">
          <mc-search-input :input-value="searchCriteria" :placeholder="searchPlaceholder" @input="handleSearchInputValueChange($event)" />
        </div>
        <div class="scroll-area">
          <div v-if="items.length && !itemsWithAppliedSearch.length" class="no-search-results-message">
            No results found for <span class="search-criteria">“{{ searchCriteria }}”</span>
          </div>
          <mc-menu-item v-for="(item, index) in itemsWithAppliedSearch" 
            :key="item.value" 
            :item="item"
            :tooltip-placement="index === 0 && !hasSearch && item.children?.length ? 'top' : 'right'"
            @click="handleMenuItemClick"/>
        </div>
      </div>
    </sl-dropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs, useSlots } from 'vue';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import McMenuItem from '@/components/ui/McMenu/McMenuItem.vue';
import McSearchInput from '@/components/ui/McSearchInput.vue';
import { IMenuItem, IMenuItemEvent } from '@/models/menu.model';
import { SlDropdown } from '@shoelace-style/shoelace';

interface IMcMenuProps {
  buttonActionIcon?: string;
  buttonActionTooltipText?: string;
  buttonActionTooltipTrigger?: string;
  buttonActionTooltipPlacement?: string;
  hasSearch?: boolean;
  items: IMenuItem[];
  menuDistance?: number;
  placement?: string;
  searchPlaceholder?: string;
}

const props = withDefaults(defineProps<IMcMenuProps>(), {
  buttonActionIcon: 'icon-three-dots',
  buttonActionTooltipText: 'More options',
  buttonActionTrigger: 'manual',
  buttonActionTooltipPlacement: 'right',
  hasSearch: false,
  menuDistance: 3,
  placement: 'right-start',
  searchPlaceholder: 'Search',
});

const {
  buttonActionIcon,
  buttonActionTooltipText,
  buttonActionTooltipTrigger,
  buttonActionTooltipPlacement,
  hasSearch,
  items,
  menuDistance,
  placement,
  searchPlaceholder
} = toRefs(props);

const slots = useSlots() as ReturnType<typeof useSlots>;
const hasButtonActionSlot = computed(() => !!slots.buttonAction);

const tooltipButtonAction = ref();
const dropdownComponent = ref({} as SlDropdown);
const isMenuOpen = ref(false);

const searchCriteria = ref("");
const itemsWithAppliedSearch = computed(() => items.value.filter(item => item.label.toLowerCase().includes(searchCriteria.value.toLowerCase())));

const emits = defineEmits([
  "click:item", 
  "close",
  "mousemove:buttonAction", 
  "mouseleave:buttonAction"]);

const openMenu = () => {
  dropdownComponent.value?.show();
  tooltipButtonAction.value?.hide();
  isMenuOpen.value = true;
};

const closeMenu = () => {
  dropdownComponent.value?.hide();
  searchCriteria.value = "";
  isMenuOpen.value = false;
  emits("close");
};

const handleButtonActionClick = () => {
  tooltipButtonAction.value?.hide()
  isMenuOpen.value ? closeMenu() : openMenu();
};

const handleDropdownHide = () => {
  isMenuOpen.value = dropdownComponent.value?.open;
};

const handleButtonActionMouseMove = (event: MouseEvent) => {
  if (!isMenuOpen.value && !tooltipButtonAction.value?.open) {
    tooltipButtonAction.value?.show();
  }
  emits("mousemove:buttonAction", event);
};

const handleButtonActionMouseLeave = (event: MouseEvent) => {
  if (tooltipButtonAction.value?.open) {
    tooltipButtonAction.value.hide();
  }
  emits("mouseleave:buttonAction", event);
};

const handleMenuItemClick = (event: IMenuItemEvent) => {
  closeMenu();
  emits("click:item", event);
};

const handleSearchInputValueChange = (value: string) => {
  searchCriteria.value = value;
};

defineExpose({
  tooltipButtonAction,
  isMenuOpen,
  openMenu,
  closeMenu,
});

</script>

<style>
:root {
  --mc-menu-width: 186px;
  --mc-menu-padding: 8px;
  --mc-menu-container-height: 100%;
  --mc-menu-background-color: var(--colour-surface-overlay);
  --mc-menu-border: border: 1px solid var(--colour-border-neutral-subtle);
  --mc-menu-border-radius: 4px;
  --mc-menu-box-shadow: var(--shadow-4);
  --mc-menu-button-action-height: 24px;
  --mc-menu-button-action-width: 24px;
  --mc-menu-button-action-background-color: transparent;
  --mc-menu-button-action-background-color-hover: var(--colour-background-neutral-hover-a-dark);
  --mc-menu-button-action-border-radius: 3px;
  --mc-menu-button-action-icon-size: 22px;
  --mc-menu-button-action-icon-color: var(--colour-icons-neutral-subtle-dark);
  --mc-menu-scroll-area-max-height: unset;
}
</style>

<style lang="scss" scoped>
.mc-menu {
  height: var(--mc-menu-container-height);

  sl-dropdown {
    width: 100%;
  }

  .menu-section {
    display: flex;
    flex-direction: column;
    width: var(--mc-menu-width);
    padding: var(--mc-menu-padding);
    background-color: var(--mc-menu-background-color);
    border: var(--mc-menu-border);
    border-radius: var(--mc-menu-border-radius);
    box-shadow: var(--mc-menu-box-shadow);

    .search-input-wrapper {
      --search-input-text-color: var(--colour-text-neutral-subtle);
      margin-bottom: 8px;
    }

    .no-search-results-message {
      color: var(--colour-text-neutral-subtle);
      font-size: var(--font-size-body-regular);
      font-weight: var(--font-weight-body-regular);
      line-height: var(--line-height-m);
      max-width: 100%;
      padding: 4px;

      .search-criteria {
        font-weight: var(--font-weight-body-medium);
        word-wrap: break-word;
      }
    }

    .scroll-area {
      display: flex;
      flex-direction: column;
      gap: 2px;
      max-height: var(--mc-menu-scroll-area-max-height);
      padding-right: 2px;
      overflow-y: auto;
    }

    ::-webkit-scrollbar {
      width: 4px; /* Adjust the width of the scrollbar */
      height: 4px;
      left: -10px;
      position: relative;
    }
    ::-webkit-scrollbar-track {
      background: rgba(0, 0, 59, 0.05); /* Change track background color */
    }
    ::-webkit-scrollbar-thumb {
      background: rgba(0, 8, 47, 0.28);
      border-radius: 4px;
    }
    ::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 8, 47, 0.4); /* Change thumb color on hover */
    }
  }

  .button-action {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    background-color: var(--mc-menu-button-action-background-color);
    width: var(--mc-menu-button-action-width);
    height: var(--mc-menu-button-action-height);
    border-radius: var(--mc-menu-button-action-border-radius);
    font-size: var(--mc-menu-button-action-icon-size);
    color: var(--mc-menu-button-action-icon-color);
    cursor: pointer;

    &:hover,
    &.active {
      background-color: var(--mc-menu-button-action-background-color-hover);
    }
  }
}
</style>